import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PrivacyNotice, ConsentBoxes } from "@/components/PrivacyNotice";
import { syncFromUser } from "@/lib/consent";

const CURRENT = "2026-06.1";

// Shown to logged-in users whose notice acceptance is missing/outdated (legacy, Google, EZ handoff) or whose age is unknown.
// Non-blocking: "Remind me later" keeps the service running; optional processing stays OFF until they opt in.
export const PrivacyGate = () => {
  const { user, updateUser } = useAuth();
  const [later, setLater] = useState(sessionStorage.getItem("ez_notice_later") === "1");
  const [dob, setDob] = useState("");
  const [guardian, setGuardian] = useState("");
  const [accept, setAccept] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [saving, setSaving] = useState(false);

  const needsNotice = !!user && user.role !== "admin" && user.privacy_notice?.version !== CURRENT;
  const needsDob = !!user && !user.dob;
  useEffect(() => {
    if (user && user.role !== "admin" && (needsNotice || needsDob) && !later && user.privacy_notice_delivered?.version !== CURRENT) {
      api.post("/privacy/notice/delivered").catch(() => {});
    }
  }, [user?._id, needsNotice, needsDob, later]); // eslint-disable-line
  if (!user || user.role === "admin") return null;
  const isChild = user.age_status === "child";
  const guardianPending = isChild && user.guardian_consent?.status !== "verified";

  if (needsNotice || needsDob) {
    if (later) return null;
    const underage = dob && age(dob) < 18;
    const submit = async (e) => {
      e.preventDefault();
      if (!accept) return;
      setSaving(true);
      try {
        const { data } = await api.post("/privacy/notice/accept", {
          dob: needsDob ? dob : undefined, guardian_email: underage ? guardian : undefined,
          consents: { marketing_email: marketing, marketing_whatsapp: marketing, analytics, advertising: analytics },
        });
        updateUser(data); syncFromUser(data);
        toast.success("Thanks — your privacy choices are saved");
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
      finally { setSaving(false); }
    };
    return (
      <Modal testid="privacy-update-modal" icon={ShieldCheck} title="Privacy update" sub={`We've updated how we handle your personal data (Privacy Notice v${CURRENT}). Please review — your account and subscription continue as normal.`}>
        <form onSubmit={submit} className="space-y-4">
          <PrivacyNotice />
          {needsDob && (
            <div>
              <label className="text-sm font-medium text-slate-300">Date of birth <span className="text-slate-500">(to apply the right protections under Indian law)</span></label>
              <input data-testid="gate-dob-input" type="date" required value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().slice(0, 10)}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/50" />
              {underage && (
                <div className="mt-3">
                  <label className="text-sm font-medium text-amber-300">Parent / guardian email <span className="text-slate-500">(required under 18 — we'll ask them to confirm)</span></label>
                  <input data-testid="gate-guardian-input" type="email" required value={guardian} onChange={(e) => setGuardian(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-amber-400/30 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none" />
                </div>
              )}
            </div>
          )}
          <ConsentBoxes accept={accept} setAccept={setAccept} marketing={marketing} setMarketing={setMarketing} showMarketing={!underage}
            acceptLabel={<>I acknowledge the updated Privacy Notice and Terms needed to keep providing my account. <span className="text-slate-500">(Required)</span></>} />
          {!underage && (
            <label className="flex items-start gap-2 text-xs text-slate-300">
              <input data-testid="gate-analytics-checkbox" type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-0.5 accent-cyan-400" />
              <span>Allow analytics & advertising-measurement cookies (Google Analytics, Meta Pixel). <span className="text-slate-500">(Optional)</span></span>
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <button data-testid="gate-continue-btn" type="submit" disabled={saving || !accept}
              className="flex items-center gap-2 rounded-xl bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#041016] disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save and continue"}</button>
            <button data-testid="gate-later-btn" type="button" onClick={() => { sessionStorage.setItem("ez_notice_later", "1"); setLater(true); }}
              className="flex items-center gap-2 rounded-xl border border-white/15 px-5 py-2.5 text-sm text-slate-300 hover:bg-white/5"><Clock className="h-4 w-4" /> Remind me later</button>
          </div>
        </form>
      </Modal>
    );
  }

  if (guardianPending) return <GuardianPending user={user} />;
  return null;
};

const GuardianPending = ({ user }) => {
  const [email, setEmail] = useState(user.guardian_email || "");
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { await api.post("/privacy/guardian/resend", { guardian_email: email }); toast.success("Consent request sent to your parent/guardian"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="guardian-pending-banner" className="mx-4 mt-4 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4 text-sm text-amber-100 sm:mx-8">
      <p className="flex items-center gap-2 font-semibold"><Mail className="h-4 w-4" /> Waiting for your parent/guardian's consent</p>
      <p className="mt-1 text-xs text-amber-200/80">Because you're under 18, the AI assessment and coach stay in limited mode until your parent or guardian confirms using the code we emailed to <b>{user.guardian_email}</b>. You can still browse colleges, scholarships and the community.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input data-testid="guardian-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white" />
        <button data-testid="guardian-resend-btn" onClick={resend} disabled={busy} className="rounded-lg bg-amber-400 px-4 py-2 text-xs font-bold text-[#1a1200] disabled:opacity-50">Resend request</button>
      </div>
    </div>
  );
};

const Modal = ({ testid, icon: Icon, title, sub, children }) => (
  <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center">
    <div data-testid={testid} className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-white/10 bg-[#0F1621] p-6 text-white shadow-2xl">
      <div className="flex items-center gap-3"><div className="rounded-xl bg-cyan-400/10 p-2"><Icon className="h-5 w-5 text-cyan-400" /></div>
        <div><h2 className="text-lg font-bold">{title}</h2><p className="text-xs text-slate-400">{sub}</p></div></div>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

const age = (dob) => { const d = new Date(dob), t = new Date(); let a = t.getFullYear() - d.getFullYear(); if (t < new Date(t.getFullYear(), d.getMonth(), d.getDate())) a--; return a; };
export { age };
